import { useState, useEffect } from "react";
import {
  AiOutlineUserAdd,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlineSearch,
  AiOutlineFileExcel,
} from "react-icons/ai";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Input,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";

import axios from "axios";
import { showToast } from "../../../utils/toast";
import { formatTableDateTime12H } from "../../../utils/dateFormatter";
import "../../../styles/dashboard/SuperAdmin/SubAdminManagement.css";
import "../../../styles/dashboard/OrgUser/CreditCheck.css";
import * as XLSX from "xlsx";

/* =======================
   CONSTANTS & HELPERS
======================= */
const API_URL = process.env.REACT_APP_API_URL;
const getToken = () => sessionStorage.getItem("token") || localStorage.getItem("token");

const authHeader = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
});

export default function SubAdminManagement({ role = "superadmin" }) {

  // Role-based permissions for SuperAdmin/Admin/SubAdmin
  const canAdd = role === "superadmin" || role === "admin" || role === "coordinateagent";
  const canEdit = role === "superadmin" || role === "admin" || role === "coordinateagent";
  const canDelete = role === "superadmin";
  const canViewDetails = true; // All roles can view
  const canToggleStatus = role === "superadmin" || role === "admin" || role === "coordinateagent";
  const canExport = role === "superadmin" || role === "admin" || role === "coordinateagent";
  const canManageCredits = role === "superadmin";
  /* =======================
     STATE
  ======================= */
  const [subAdmins, setSubAdmins] = useState([]);
  const [filteredSubAdmins, setFilteredSubAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const subAdminsPerPage = 10;
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(""); // add | edit | view | delete
  const [selectedSubAdmin, setSelectedSubAdmin] = useState(null);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    role: "coordinateagent",
    username: "",
    password: "",
    address: "",
    city: "",
    taluka: "",
    district: "",
    state: "",
    aadhaarNumber: "",
    panNumber: "",
    employeeId: "",
    referralAgentCode: "",
    referralAgentName: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /* =======================
     API FUNCTIONS (Role-aware)
  ======================= */
  const getSubAdmins = () => {
    const endpoint = role === "admin" 
      ? `${API_URL}/admin/sub-admins`
      : `${API_URL}/superadmin/sub-admin`;
    return axios.get(endpoint, authHeader());
  };

  const createSubAdmin = (payload) => {
    const endpoint = role === "admin" 
      ? `${API_URL}/admin/sub-admins`
      : `${API_URL}/superadmin/sub-admin`;
    return axios.post(endpoint, payload, authHeader());
  };

  const updateSubAdmin = (id, payload) => {
    const endpoint = role === "admin" 
      ? `${API_URL}/admin/sub-admins/${id}`
      : `${API_URL}/superadmin/sub-admin/${id}`;
    return axios.put(endpoint, payload, authHeader());
  };
  
  const updateSubAdminStatus = (id, isActive) => {
    const statusEndpoint = role === "admin" 
      ? `${API_URL}/admin/sub-admins/${id}/status`
      : `${API_URL}/superadmin/sub-admin/${id}/status`;
    return axios.patch(statusEndpoint, { isActive }, authHeader());
  };

  const deleteSubAdmin = (id) => {
    const endpoint = role === "admin" 
      ? `${API_URL}/admin/sub-admins/${id}`
      : `${API_URL}/superadmin/sub-admin/${id}`;
    return axios.delete(endpoint, authHeader());
  };

  /* =======================
     FETCH DATA
  ======================= */
  const fetchSubAdmins = async () => {
    console.log('🚀 Starting fetchSubAdmins with role:', role);
    setLoading(true);
    try {
      const res = await getSubAdmins();
      console.log('📦 SubAdmins API response:', res);
      console.log('📦 Response data structure:', res?.data);
      const subAdminsList = res?.data?.data || [];
      console.log('📦 SubAdmins list:', subAdminsList);
      setSubAdmins(subAdminsList);
      setFilteredSubAdmins(subAdminsList);
    } catch (error) {
      console.error('❌ SubAdmins fetch error:', error);
      console.error('❌ Error response:', error?.response?.data);
      showToast("Failed to load Sub-Admins", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubAdmins();
  }, []);

  /* =======================
     SEARCH
  ======================= */
  useEffect(() => {
    let filtered = subAdmins.filter(
      (sa) =>
        sa.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sa.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sa.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sa.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply status filter
    if (filterStatus !== "all") {
      const isActive = filterStatus === "Active";
      filtered = filtered.filter((sa) => sa.isActive === isActive);
    }

    // Apply date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter((sa) => {
        const createdDate = new Date(sa.createdAt);
        return createdDate >= fromDate;
      });
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((sa) => {
        const createdDate = new Date(sa.createdAt);
        return createdDate <= toDate;
      });
    }

    setFilteredSubAdmins(filtered);
    setCurrentPage(1);
  }, [searchTerm, filterStatus, dateFrom, dateTo, subAdmins]);

  /* =======================
     VALIDATION
  ======================= */
  // const validateField = (name, value) => {
  //   const val = value?.toString().trim() || "";
  //   switch (name) {
  //     case "fullName":
  //       if (!val) return "Full name is required";
  //       break;
  //     case "email":
  //       if (!val) return "Email is required";
  //       if (!/^\S+@\S+\.\S+$/.test(val)) return "Invalid email";
  //       break;
  //     case "username":
  //       if (!val) return "Username is required";
  //       break;
  //     case "mobileNumber":
  //       if (!/^\d{10}$/.test(val)) return "Mobile must be 10 digits";
  //       break;
  //     case "aadhaarNumber":
  //       if (val && !/^\d{12}$/.test(val)) return "Aadhaar must be 12 digits";
  //       break;
  //     case "panNumber":
  //       if (
  //         val &&
  //         !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val.toUpperCase())
  //       )
  //         return "Invalid PAN format";
  //       break;
  //     case "password":
  //       if (modalMode === "add" && !val) return "Password is required";
  //       if (val && val.length < 6) return "Min 6 characters";
  //       break;
  //     default:
  //       break;
  //   }
  //   return "";
  // };
  const REQUIRED_FIELDS = [
    "fullName",
    "username",
    "email",
    "mobileNumber",
    "aadhaarNumber",
    "panNumber",
  ];

  const validateField = (name, value) => {
    const val = value?.toString().trim() || "";

    // Password is only required in "add" mode
    if (name === "password" && modalMode === "add" && !val) {
      return "Password is required";
    }

    // Other required field check (excluding password in edit mode)
    if (REQUIRED_FIELDS.includes(name) && !val) {
      return `${name
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (s) => s.toUpperCase())} is required`;
    }

    switch (name) {
      case "email":
        if (val && !/^\S+@\S+\.\S+$/.test(val)) return "Invalid email";
        break;

      case "mobileNumber":
        if (val && !/^\d{10}$/.test(val)) return "Mobile must be 10 digits";
        break;

      case "aadhaarNumber":
        if (val && !/^\d{12}$/.test(val)) return "Aadhaar must be 12 digits";
        break;

      case "panNumber":
        if (val && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val.toUpperCase()))
          return "Invalid PAN format";
        break;

      case "password":
        if (val && val.length < 6) return "Min 6 characters";
        break;

      default:
        break;
    }

    return "";
  };

  // const validateForm = () => {
  //   const newErrors = {};
  //   Object.keys(formData).forEach((field) => {
  //     const err = validateField(field, formData[field]);
  //     if (err) newErrors[field] = err;
  //   });
  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };
  const validateForm = () => {
    const newErrors = {};

    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // const handleInputChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({ ...prev, [name]: value }));
  //   setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  // };
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  /* =======================
     MODAL CONTROL
  ======================= */
  const openModal = (mode, data = null) => {
    setModalMode(mode);
    setSelectedSubAdmin(data);
    setErrors({});
    setShowPassword(false);

    if (mode === "add") {
      setFormData({
        fullName: "",
        email: "",
        mobileNumber: "",
        role: "coordinateagent",
        username: "",
        password: "",
        address: "",
        city: "",
        taluka: "",
        district: "",
        state: "",
        aadhaarNumber: "",
        panNumber: "",
        employeeId: "",
        referralAgentCode: "",
        referralAgentName: "",
      });
    }

    if (mode === "edit" && data) {
      // Don't include password when editing - only send if user changes it
      setFormData({
        fullName: data.fullName || "",
        email: data.email || "",
        mobileNumber: data.mobileNumber || "",
        role: data.role || "coordinateagent",
        username: data.username || "",
        password: "", // Empty - only update if user enters new password
        address: data.address || "",
        city: data.city || "",
        taluka: data.taluka || "",
        district: data.district || "",
        state: data.state || "",
        aadhaarNumber: data.aadhaarNumber || "",
        panNumber: data.panNumber || "",
        employeeId: data.employeeId || "",
        referralAgentCode: data.referralAgentCode || "",
        referralAgentName: data.referralAgentName || "",
      });
    }

    if (mode === "view" && data) {
      setFormData({
        fullName: data.fullName || "",
        email: data.email || "",
        mobileNumber: data.mobileNumber || "",
        role: data.role || "coordinateagent",
        username: data.username || "",
        password: "",
        address: data.address || "",
        city: data.city || "",
        taluka: data.taluka || "",
        district: data.district || "",
        state: data.state || "",
        aadhaarNumber: data.aadhaarNumber || "",
        panNumber: data.panNumber || "",
        employeeId: data.employeeId || "",
        referralAgentCode: data.referralAgentCode || "",
        referralAgentName: data.referralAgentName || "",
      });
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMode("");
    setSelectedSubAdmin(null);
  };

  /* =======================
     SUBMIT
  ======================= */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      let shouldRefetch = false;

      if (modalMode === "add") {
        const response = await createSubAdmin(formData);
        console.log('✅ Create response:', response);
        showToast("Sub-Admin created successfully", "success");
        
        // Use the complete SubAdmin object from the response
        const newSubAdmin = response?.data?.subAdmin || {
          ...formData,
          id: Date.now().toString(),
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        console.log('Adding to list:', newSubAdmin);
        setSubAdmins(prev => {
          const updated = [newSubAdmin, ...prev];
          console.log('Updated subAdmins state:', updated);
          return updated;
        });
        setFilteredSubAdmins(prev => [newSubAdmin, ...prev]);
        shouldRefetch = true;
      }

      if (modalMode === "edit") {
        // In edit mode, don't send password if it's empty (user didn't change it)
        const dataToSend = { ...formData };
        if (!dataToSend.password) {
          delete dataToSend.password;
        }
        const response = await updateSubAdmin(selectedSubAdmin.id, dataToSend);
        console.log('✅ Update response:', response);
        showToast("Sub-Admin updated successfully", "success");
        
        // Optimistic UI update - update the SubAdmin in the list
        const updatedData = { ...selectedSubAdmin, ...formData, updatedAt: new Date().toISOString() };
        setSubAdmins(prev =>
          prev.map(sa =>
            sa.id === selectedSubAdmin.id ? updatedData : sa
          )
        );
        setFilteredSubAdmins(prev =>
          prev.map(sa =>
            sa.id === selectedSubAdmin.id ? updatedData : sa
          )
        );
        shouldRefetch = true;
      }

      closeModal();
      setFormData({
        fullName: "",
        email: "",
        mobileNumber: "",
        role: "coordinateagent",
        username: "",
        password: "",
        address: "",
        city: "",
        taluka: "",
        district: "",
        state: "",
        aadhaarNumber: "",
        panNumber: "",
        employeeId: "",
        referralAgentCode: "",
        referralAgentName: "",
      });

      // Refresh data in background after a delay to sync with backend
      if (shouldRefetch) {
        setTimeout(() => {
          console.log('🔄 Refetching SubAdmins in background');
          fetchSubAdmins();
        }, 1000);
      }
    } catch (err) {
      console.error('❌ Submit error:', err);
      const data = err?.response?.data;
      const baseMessage = data?.message || "Action failed";
      const detail = data?.error && data.error !== baseMessage ? ` (${data.error})` : "";
      showToast(`${baseMessage}${detail}`, "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSubAdmin(selectedSubAdmin.id);
      showToast("Sub-Admin deleted successfully", "success");
      
      // Optimistic UI update - remove from list immediately
      setSubAdmins(prev => prev.filter(sa => sa.id !== selectedSubAdmin.id));
      setFilteredSubAdmins(prev => prev.filter(sa => sa.id !== selectedSubAdmin.id));
      
      closeModal();
      
      // Refresh data in background
      setTimeout(() => {
        console.log('🔄 Refetching SubAdmins after delete');
        fetchSubAdmins();
      }, 1000);
    } catch (err) {
      console.error('❌ Delete error:', err);
      showToast("Delete failed", "danger");
    }
  };

  /* =======================
     Change Stauts 
  ======================= */

  const openStatusModal = (subAdmin) => {
    console.log("Opening modal for:", subAdmin);
    setSelectedSubAdmin(subAdmin);
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setSelectedSubAdmin(null);
  };

  const confirmStatusChange = async () => {
    if (!selectedSubAdmin) return;

    const newStatus = !selectedSubAdmin.isActive;
    setStatusLoading(true);

    try {
      await updateSubAdminStatus(selectedSubAdmin.id, newStatus);

      showToast(
        `Sub-Admin ${newStatus ? "activated" : "deactivated"} successfully`,
        "success"
      );

      // Optimistic UI update
      setSubAdmins((prev) =>
        prev.map((sa) =>
          sa.id === selectedSubAdmin.id ? { ...sa, isActive: newStatus } : sa
        )
      );

      closeStatusModal();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Status update failed",
        "danger"
      );
    } finally {
      setStatusLoading(false);
    }
  };

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = filteredSubAdmins.map((sa, idx) => ({
      "Sr No.": idx + 1,
      "Full Name": sa.fullName || "-",
      "Username": sa.username || "-",
      "Email": sa.email || "-",
      "Mobile Number": sa.mobileNumber || "-",
      "Agent Code": sa.employeeId || "-",
      "Referral Agent Code": sa.referralAgentCode || "-",
      "Referral Agent Name": sa.referralAgentName || "-",
      "Address": sa.address || "-",
      "City/Village": sa.city || "-",
      "Taluka": sa.taluka || "-",
      "District": sa.district || "-",
      "State": sa.state || "-",
      "Aadhaar Number": sa.aadhaarNumber || "-",
      "PAN Number": sa.panNumber || "-",
      "Role": sa.role || "-",
      "Status": sa.status || "-",
      "Active": sa.isActive ? "Yes" : "No",
      "Created Date": formatTableDateTime12H(sa.createdAt),
      "Updated Date": formatTableDateTime12H(sa.updatedAt),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths for better visibility
    const columnWidths = [
      { wch: 8 },  // Sr No.
      { wch: 20 }, // Full Name
      { wch: 15 }, // Username
      { wch: 25 }, // Email
      { wch: 15 }, // Mobile Number
      { wch: 15 }, // Agent Code
      { wch: 18 }, // Referral Agent Code
      { wch: 24 }, // Referral Agent Name
      { wch: 25 }, // Address
      { wch: 15 }, // City/Village
      { wch: 15 }, // Taluka
      { wch: 15 }, // District
      { wch: 15 }, // State
      { wch: 15 }, // Aadhaar Number
      { wch: 15 }, // PAN Number
      { wch: 12 }, // Role
      { wch: 12 }, // Status
      { wch: 10 }, // Active
      { wch: 20 }, // Created Date
      { wch: 20 }, // Updated Date
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SubAdmins");
    
    const fileName = `SubAdmin_Management_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    showToast("Data exported successfully!", "success");
  };

  // Pagination
  const indexOfLast = currentPage * subAdminsPerPage;
  const indexOfFirst = indexOfLast - subAdminsPerPage;
  const currentSubAdmins = filteredSubAdmins.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredSubAdmins.length / subAdminsPerPage);

  /* =======================
     RENDER
  ======================= */
  return (
    <div className="sam-container">
      <div className="sam-header">
        <div>
          <h2>Coordinate Agent Management</h2>
          <p className="text-muted">Manage coordinate agent accounts and permissions</p>
        </div>
      </div>

      <div className="sam-controls">
        <div className="sam-search-bar">
          <AiOutlineSearch className="sam-search-icon" />
          <input
            autoComplete="new-password"
            type="text"
            placeholder="Search by name, email, username, agent code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="sam-search-input"
          />
        </div>
      </div>

      <div className="sam-filter-row">
        <div className="sam-filter-group">
          <div className="sam-date-filter">
            <label className="sam-filter-label">Date Range:</label>
            <div className="sam-date-inputs">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="sam-date-input"
                placeholder="From Date"
              />
              <span className="sam-date-separator">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="sam-date-input"
                placeholder="To Date"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="sam-filter-select"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="sam-action-buttons">
          <button className="sam-export-btn" onClick={handleExportToExcel}>
            <AiOutlineFileExcel /> Export
          </button>
          <button className="sam-add-btn" onClick={() => openModal("add")}> 
            <AiOutlineUserAdd /> Add Coordinate Agent
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="sam-table-wrapper">
        {loading ? (
          <div className="sam-loading-container">
            <Spinner />
          </div>
        ) : (
          <table className="sam-table">
            <thead>
              <tr>
                <th>Sr No.</th>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Role</th>
                <th>Username</th>
                <th>Agent Code</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentSubAdmins.length === 0 ? (
                <tr>
                  <td colSpan="11" className="sam-no-data">
                    {subAdmins.length === 0 ? "Loading..." : "No coordinate agents found"}
                  </td>
                </tr>
              ) : (
                currentSubAdmins.map((sa, idx) => (
                  <tr key={sa.id}>
                    <td>{indexOfFirst + idx + 1}</td>
                    <td className="sam-name">{sa.fullName}</td>
                    <td className="sam-email">{sa.email}</td>
                    <td>{sa.mobileNumber}</td>
                    <td>
                      <span className="sam-role-badge">
                        {(sa.role === "coordinateagent" || sa.role === "subadmin" || sa.role === "SubAdmin") ? "Coordinate Agent" : sa.role}
                      </span>
                    </td>
                    <td>{sa.username}</td>
                    <td className="sam-emp-id">{sa.employeeId}</td>
                    <td>{formatTableDateTime12H(sa.createdAt)}</td>
                    <td>{formatTableDateTime12H(sa.updatedAt)}</td>
                    <td>
                      <select
                        value={sa.isActive ? "Active" : "Inactive"}
                        onChange={() => openStatusModal(sa)}
                        className={`sam-status-select ${sa.isActive ? "active" : "inactive"}`}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </td>
                    <td>
                      <div className="sam-actions">
                        <button className="sam-action-btn view" onClick={() => openModal("view", sa)} title="View Details">
                          <AiOutlineEye size={18} />
                        </button>
                        <button className="sam-action-btn edit" onClick={() => openModal("edit", sa)} title="Edit">
                          <AiOutlineEdit size={18} />
                        </button>
                        {canDelete && (
                          <button className="sam-action-btn delete" onClick={() => openModal("delete", sa)} title="Delete">
                          <AiOutlineDelete size={18} />
                        </button>)}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="sam-pagination-wrapper">
          <div className="sam-pagination-info">
            Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredSubAdmins.length)} of {filteredSubAdmins.length} entries
          </div>
          <div className="sam-pagination">
            <button 
              className="sam-page-btn" 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`sam-page-btn ${i + 1 === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className="sam-page-btn" 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* MODAL */}
      {modalOpen && (
        <div className="credit-modal-overlay" >
          <div className="credit-modal-content wide" onClick={e => e.stopPropagation()}>
            <div className="credit-modal-header">
              <h2>
                {modalMode === "add"
                  ? "Add Coordinate Agent"
                  : modalMode === "edit"
                  ? "Edit Coordinate Agent"
                  : modalMode === "view"
                  ? "Coordinate Agent Details"
                  : "Delete Coordinate Agent"}
              </h2>
              <button className="credit-modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="credit-modal-body">
          {modalMode === "delete" ? (
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedSubAdmin?.fullName}</strong>?
            </p>
          ) : modalMode === "view" ? (
            <>
              {/* Basic Information */}
              <h6 className="sam-category-title">Basic Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Full Name</Label>
                    <Input value={selectedSubAdmin?.fullName || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Username</Label>
                    <Input value={selectedSubAdmin?.username || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Role</Label>
                    <Input value={(selectedSubAdmin?.role === "coordinateagent" || selectedSubAdmin?.role === "subadmin" || selectedSubAdmin?.role === "SubAdmin") ? "Coordinate Agent" : (selectedSubAdmin?.role || "-")} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Agent Code</Label>
                    <Input value={selectedSubAdmin?.employeeId || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Referral Agent Code</Label>
                    <Input value={selectedSubAdmin?.referralAgentCode || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Referral Agent Name</Label>
                    <Input value={selectedSubAdmin?.referralAgentName || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Address</Label>
                    <Input value={selectedSubAdmin?.address || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">City/Village</Label>
                    <Input value={selectedSubAdmin?.city || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Taluka</Label>
                    <Input value={selectedSubAdmin?.taluka || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">District</Label>
                    <Input value={selectedSubAdmin?.district || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">State</Label>
                    <Input value={selectedSubAdmin?.state || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Aadhaar Number</Label>
                    <Input value={selectedSubAdmin?.aadhaarNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">PAN Number</Label>
                    <Input value={selectedSubAdmin?.panNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>
              </Row>

              {/* Contact Information */}
              <h6 className="sam-category-title">Contact Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Email</Label>
                    <Input value={selectedSubAdmin?.email || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Mobile Number</Label>
                    <Input autoComplete="off" value={selectedSubAdmin?.mobileNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>
              </Row>

              {/* System Information */}
              <h6 className="sam-category-title">System Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Status</Label>
                    <Input value={selectedSubAdmin?.isActive ? "Active" : "Inactive"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Created At</Label>
                    <Input value={formatTableDateTime12H(selectedSubAdmin?.createdAt)} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="sam-form-label">Updated At</Label>
                    <Input value={formatTableDateTime12H(selectedSubAdmin?.updatedAt)} readOnly disabled />
                  </FormGroup>
                </Col>
              </Row>
            </>
          ) : (
            <Row>
              {/* Full Name */}
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Full Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    invalid={!!errors.fullName}
                  />
                  {errors.fullName && (
                    <small className="text-danger">{errors.fullName}</small>
                  )}
                </FormGroup>
              </Col>

              {/* Username */}
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Username <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="username"
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleInputChange}
                    invalid={!!errors.username}
                  />
                </FormGroup>
              </Col>

              {/* Email */}
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Email <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    invalid={!!errors.email}
                  />
                </FormGroup>
              </Col>

              {/* Mobile */}
              <Col md={6}>
                <FormGroup>
                  <Label>Mobile Number</Label>
                  <Input
                    autoComplete="off"
                    name="mobileNumber"
                    placeholder="Mobile (10 digits)"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    invalid={!!errors.username}
                  />
                </FormGroup>
              </Col>

              {/* Role */}
              <Col md={6}>
                <FormGroup>
                  <Label>Role</Label>
                  <Input type="select" value="Coordinate Agent" disabled>
                    <option>Coordinate Agent</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Agent Code</Label>
                  <Input
                    name="employeeId"
                    placeholder="Auto-generated"
                    value={formData.employeeId || ""}
                    disabled
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Referral Agent Code</Label>
                  <Input
                    name="referralAgentCode"
                    placeholder="Referral Agent Code (optional)"
                    value={formData.referralAgentCode}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Referral Agent Name</Label>
                  <Input
                    name="referralAgentName"
                    placeholder="Referral Agent Name (optional)"
                    value={formData.referralAgentName}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              {/* Password */}
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Password{" "}
                    {modalMode === "add" && (
                      <span className="text-danger">*</span>
                    )}
                  </Label>
                  <div className="position-relative">
                    <Input
                      autoComplete="new-password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleInputChange}
                      invalid={!!errors.username}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="sam-password-toggle"
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible />
                      ) : (
                        <AiOutlineEye />
                      )}
                    </span>
                  </div>
                </FormGroup>
              </Col>

              {/* Address */}
              <Col md={6}>
                <FormGroup>
                  <Label>Address</Label>
                  <Input
                    name="address"
                    placeholder="Address"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              {/* City/Village */}
              <Col md={6}>
                <FormGroup>
                  <Label>City/Village</Label>
                  <Input
                    name="city"
                    placeholder="City/Village"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              {/* Taluka */}
              <Col md={6}>
                <FormGroup>
                  <Label>Taluka</Label>
                  <Input
                    name="taluka"
                    placeholder="Taluka/Subdivision"
                    value={formData.taluka}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              {/* District */}
              <Col md={6}>
                <FormGroup>
                  <Label>District</Label>
                  <Input
                    name="district"
                    placeholder="District"
                    value={formData.district}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              {/* State */}
              <Col md={6}>
                <FormGroup>
                  <Label>State</Label>
                  <Input
                    name="state"
                    placeholder="State"
                    value={formData.state}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              {/* Aadhaar */}
              <Col md={6}>
                <FormGroup>
                  <Label>Aadhaar Number</Label>
                  <Input
                    name="aadhaarNumber"
                    placeholder="Aadhaar (12 digits)"
                    value={formData.aadhaarNumber}
                    onChange={handleInputChange}
                    invalid={!!errors.username}
                  />
                </FormGroup>
              </Col>

              {/* PAN */}
              <Col md={6}>
                <FormGroup>
                  <Label>PAN Number</Label>
                  <Input
                    name="panNumber"
                    placeholder="PAN (e.g. ABCDE1234F)"
                    value={formData.panNumber}
                    onChange={handleInputChange}
                    invalid={!!errors.username}
                  />
                </FormGroup>
              </Col>

            </Row>
          )}
        </div>

        <div className="credit-modal-footer">
          {modalMode !== "view" && modalMode !== "delete" && (
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {modalMode === "add" ? "Create" : "Update"}
            </Button>
          )}
          {modalMode === "delete" && (
            <Button color="danger" onClick={handleDelete}>
              Delete
            </Button>
          )}
          <Button color="secondary" onClick={closeModal}>
            Close
          </Button>
        </div>
      </div>
    </div>
      )}
      {/*  Status Change modal */}
      {/* Status Change Modal */}
      <Modal isOpen={statusModalOpen} toggle={closeStatusModal} centered>
        <ModalHeader toggle={closeStatusModal}>
          {selectedSubAdmin?.isActive ? "Deactivate" : "Activate"} Sub-Admin
        </ModalHeader>

        <ModalBody>
          <p>
            Are you sure you want to{" "}
            <strong>
              {selectedSubAdmin?.isActive ? "deactivate" : "activate"}
            </strong>{" "}
            <strong>{selectedSubAdmin?.fullName}</strong>?
          </p>

          {selectedSubAdmin?.isActive && (
            <p className="text-danger mt-2">
              This user will lose access to the system.
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="secondary"
            onClick={closeStatusModal}
            disabled={statusLoading}
          >
            Cancel
          </Button>

          <Button
            color={selectedSubAdmin?.isActive ? "danger" : "success"}
            onClick={confirmStatusChange}
            disabled={statusLoading}
          >
            {statusLoading
              ? "Please wait..."
              : selectedSubAdmin?.isActive
              ? "Deactivate"
              : "Activate"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

